import instanceAxios from '@/util/axios';
import { AgentContextEntity, AgentStreamEvent, ChatTurn } from './chat.types';

/**
 * Streams one chat turn.
 *
 * Uses `fetch` rather than the shared axios instance because axios cannot consume a streaming
 * response body in the browser. That means the auth header has to be attached by hand — the
 * axios interceptor never runs here — and it also means a 401 does not trigger the interceptor's
 * redirect to login, so it is handled explicitly below.
 */
export const streamAgentChat = async (
  messages: ChatTurn[],
  /** Records carried over from earlier turns — see `AgentContextEntity`. */
  context: AgentContextEntity[],
  /** Minted client-side per conversation, so the stored log reads as a sequence. */
  conversationId: string,
  onEvent: (event: AgentStreamEvent) => void,
  signal?: AbortSignal
): Promise<void> => {
  const response = await fetch(`${instanceAxios.defaults.baseURL}pvt/agent/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem('token') || ''
    },
    body: JSON.stringify({ messages, context, conversationId }),
    signal
  });

  if (response.status === 401) {
    // Mirrors what the axios interceptor would have done for a normal request.
    window.localStorage.removeItem('token');
    window.location.replace('/login');
    return;
  }

  if (response.status === 403) {
    throw new Error('אין לך הרשאה להשתמש בצ׳אט. פנה למנהל המערכת.');
  }

  if (!response.ok || !response.body) {
    throw new Error('שגיאה בשליחת ההודעה');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emit = (frame: string) => {
    const line = frame.split('\n').find(part => part.startsWith('data:'));
    if (!line) return;
    const payload = line.slice(5).trim();
    if (!payload) return;
    try {
      onEvent(JSON.parse(payload) as AgentStreamEvent);
    } catch {
      // A truncated frame on a dropped connection — nothing useful to salvage.
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        emit(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
};
